import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle, AlertTriangle, ArrowRight, BellRing, CalendarClock, CalendarRange,
  CheckCircle2, ChevronLeft, ChevronRight, Circle, Download, FileArchive, FileText,
  HelpCircle, Info, Loader2, Minus, Send, ShieldCheck, Sparkles, Stamp,
  TrendingDown, TrendingUp, X, XCircle,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppShell from "../components/AppShell";
import { api, API_URL } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const STATUS_LABEL = {
  ready: { label: "Lista", tone: "success" },
  partial: { label: "Faltan datos", tone: "warn" },
  review: { label: "Revisar", tone: "warn" },
  empty: { label: "Sin registros", tone: "muted" },
  prepared: { label: "Preparada", tone: "success" },
  presented: { label: "Presentada manualmente", tone: "success" },
};

const HELP_TEXTS = {
  isr: "Es un impuesto relacionado con tus ingresos. El cálculo depende de tu régimen fiscal y situación. Es solo una estimación.",
  iva: "El IVA (Impuesto al Valor Agregado) grava ciertas ventas y servicios. Restamos el IVA que pagaste en gastos deducibles.",
  retenciones: "Son impuestos que un cliente u otra persona te retuvo y reportó al SAT en tu nombre. Se restan de tu total a pagar.",
  saldo_favor: "Cuando lo que te retuvieron es mayor a lo que debes, el SAT te queda debiendo. Podrías solicitar devolución o acreditarlo el próximo mes.",
  regime: "Tu régimen fiscal determina qué reglas aplican para tus impuestos. Aparece en tu Constancia de Situación Fiscal (SAT).",
};

function StatusBadge({ status, onClick, testid }) {
  const meta = STATUS_LABEL[status] || STATUS_LABEL.review;
  return (
    <button
      type="button"
      className={`decl-status decl-status-${meta.tone}`}
      onClick={onClick}
      data-testid={testid}
    >
      {meta.tone === "success" ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {meta.label}
    </button>
  );
}

function Tip({ text, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="decl-tip">
      <button
        type="button"
        className="decl-tip-btn"
        aria-label="¿Qué significa esto?"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
      >
        <HelpCircle size={13} />
      </button>
      {open && <span className="decl-tip-body" role="tooltip"><Info size={12} /><span>{text}</span></span>}
    </span>
  );
}

function AlertItem({ alert, onAction }) {
  const level = alert.level;
  const Icon = level === "ok" ? CheckCircle2 : level === "critical" ? AlertCircle : AlertTriangle;
  return (
    <li className={`decl-alert decl-alert-${level}`} data-testid={`declarations-alert-${alert.key}`}>
      <Icon size={16} />
      <span>{alert.message}</span>
      {alert.action && alert.action.kind === "navigate" && (
        <button type="button" onClick={() => onAction(alert.action.to)} data-testid={`declarations-alert-cta-${alert.key}`}>
          Revisar <ArrowRight size={12} />
        </button>
      )}
    </li>
  );
}

export default function DeclarationsPage() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState("");
  const [showChecklist, setShowChecklist] = useState(false);
  const [showPrepare, setShowPrepare] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showPresented, setShowPresented] = useState(null); // holds declaration id
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [prepareAnswers, setPrepareAnswers] = useState({ declare_zero_income: false, declare_zero_expenses: false });
  const [ack, setAck] = useState({ ingresos: false, gastos: false, facturas: false, movimientos: false, estimacion: false });
  const currentYear = new Date().getFullYear();
  const [ytdYear, setYtdYear] = useState(currentYear);
  const [ytd, setYtd] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [reminderMsg, setReminderMsg] = useState("");

  const loadYtd = useCallback(async (year) => {
    try {
      const { data } = await api.get(`/declarations/ytd?year=${year}`, { withCredentials: true });
      setYtd(data);
    } catch (_) { setYtd(null); }
  }, []);
  useEffect(() => { loadYtd(ytdYear); }, [ytdYear, loadYtd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: prev }, { data: list }] = await Promise.all([
        api.get(`/declarations/preview?period=${period}`, { withCredentials: true }),
        api.get("/declarations", { withCredentials: true }),
      ]);
      setPreview(prev);
      setPrepareAnswers(prev.answers || { declare_zero_income: false, declare_zero_expenses: false });
      setHistory(list || []);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar las declaraciones.");
    } finally {
      setLoading(false);
    }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const shiftPeriod = (delta) => {
    const [y, m] = period.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const monthOptions = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}` });
    }
    return out;
  }, []);

  const canPrepare = ack.ingresos && ack.gastos && ack.facturas && ack.movimientos && ack.estimacion;

  const prepare = async () => {
    if (!canPrepare) return;
    setPreparing(true);
    setError("");
    try {
      const body = { period: preview.period, ...prepareAnswers };
      const response = await api.post("/declarations/prepare", body, { withCredentials: true });
      setShowPrepare(false);
      setAck({ ingresos: false, gastos: false, facturas: false, movimientos: false, estimacion: false });
      setPreview((current) => ({ ...current, ...response.data, saved: true }));
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos preparar la declaración.");
    } finally {
      setPreparing(false);
    }
  };

  const downloadPdf = async (id, per) => {
    const token = window.localStorage.getItem("session_token");
    const resp = await fetch(`${API_URL}/declarations/${id}/pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `declaracion-${per}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadXmlPack = async (id, per) => {
    const token = window.localStorage.getItem("session_token");
    const resp = await fetch(`${API_URL}/declarations/${id}/xml-pack`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      setError("No pudimos generar el paquete XML.");
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `declaracion-${per}-xml-pack.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openDetail = async (id) => {
    setShowDetail(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/declarations/${id}/detail`, { withCredentials: true });
      setDetail(data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos abrir el detalle.");
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const markPresented = async (id, receiptNo) => {
    try {
      await api.post(`/declarations/${id}/mark-presented`, { receipt_no: receiptNo || "" }, { withCredentials: true });
      setShowPresented(null);
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos marcar la declaración.");
    }
  };

  const addReminder = async (offsetDays) => {
    if (!preview?.id) {
      setReminderMsg("Primero prepara la declaración para poder crear el recordatorio.");
      return;
    }
    try {
      await api.post(`/declarations/${preview.id}/remind`, { offset_days: offsetDays || 0 }, { withCredentials: true });
      setReminderMsg("Listo. Añadimos el recordatorio a tu Calendario.");
      setTimeout(() => setReminderMsg(""), 4000);
    } catch (issue) {
      setReminderMsg(issue.response?.data?.detail || "No pudimos agregar el recordatorio.");
    }
  };

  const historyByYear = useMemo(() => {
    const map = new Map();
    history.forEach((row) => {
      const list = map.get(row.year) || [];
      list.push(row);
      map.set(row.year, list);
    });
    return Array.from(map.entries()).sort(([a], [b]) => b - a);
  }, [history]);

  if (loading) {
    return <AppShell><div className="page"><div className="import-loading"><Loader2 className="spin" size={22} /><span>Revisando tus movimientos...</span></div></div></AppShell>;
  }

  const s = preview?.summary || {};
  const fp = preview?.fiscal_profile || {};
  const dl = preview?.deadline || {};
  const alerts = preview?.alerts || [];
  const hasSaldoFavor = (s.saldo_a_favor || 0) > 0;

  return (
    <AppShell>
      <div className="page declarations-page">
        <section className="decl-hero">
          <div>
            <small className="eyebrow">DECLARACIÓN MENSUAL</small>
            <h1>Tu contador, <em>siempre listo.</em></h1>
            <p>Preparamos tu declaración con tus CFDI, ingresos, egresos, caja y comprobantes del mes.</p>
          </div>
        </section>

        {error && <p className="profile-error" data-testid="declarations-error">{error}</p>}

        {/* Period selector */}
        <section className="decl-period" data-testid="declarations-period">
          <button type="button" onClick={() => shiftPeriod(-1)} data-testid="declarations-period-prev" aria-label="Mes anterior">
            <ChevronLeft size={16} />
          </button>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="declarations-period-select"
          >
            {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => shiftPeriod(1)}
            disabled={period >= monthOptions[0].value}
            data-testid="declarations-period-next"
            aria-label="Mes siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </section>

        {/* Fiscal profile card */}
        <section className="decl-fiscal-card" data-testid="declarations-fiscal-profile">
          <header>
            <ShieldCheck size={16} />
            <div>
              <small>TU RÉGIMEN FISCAL</small>
              <b>{fp.regime}</b>
            </div>
            <Tip text={HELP_TEXTS.regime} testid="declarations-tip-regime" />
          </header>
          <ul>
            <li><span>Nombre / Razón social</span><b>{fp.name}</b></li>
            <li><span>RFC</span><b>{fp.rfc}</b></li>
            <li><span>Actividad</span><b>{fp.activity}</b></li>
            <li><span>Tipo de contribuyente</span><b>{fp.contributor_type}</b></li>
          </ul>
          <small className="decl-disclaimer">Los cálculos dependen del régimen y las obligaciones registradas en tu perfil.</small>
        </section>

        {/* Deadline card */}
        <section className={`decl-deadline-card ${dl.overdue ? "overdue" : dl.days_left <= 7 ? "urgent" : ""}`} data-testid="declarations-deadline-card">
          <div>
            <CalendarClock size={18} />
            <div>
              <small>PRÓXIMA FECHA LÍMITE</small>
              <b>{dl.label || "—"}</b>
              {typeof dl.days_left === "number" && (
                <em>{dl.overdue ? `Vencida hace ${Math.abs(dl.days_left)} día(s)` : `Faltan ${dl.days_left} día(s)`}</em>
              )}
            </div>
          </div>
          <div className="decl-deadline-actions">
            <button type="button" className="cash-btn ghost" onClick={() => addReminder(0)} data-testid="declarations-remind-day">
              <BellRing size={13} /> Recordar el día
            </button>
            <button type="button" className="cash-btn ghost" onClick={() => addReminder(3)} data-testid="declarations-remind-3d">
              <BellRing size={13} /> 3 días antes
            </button>
            <button type="button" className="cash-btn ghost" onClick={() => navigate("/calendario")} data-testid="declarations-open-calendar">
              <CalendarRange size={13} /> Ver calendario
            </button>
          </div>
          {reminderMsg && <p className="decl-reminder-msg" data-testid="declarations-reminder-msg">{reminderMsg}</p>}
        </section>

        {preview && (
          <section className="decl-current" data-testid="declarations-current">
            <header>
              <div>
                <small>{preview.period_label} · Régimen {preview.regime}</small>
                <h2>Declaración provisional</h2>
              </div>
              <StatusBadge status={preview.status} onClick={() => setShowChecklist(true)} testid="declarations-status-badge" />
            </header>

            <div className="decl-progress" data-testid="declarations-progress">
              <div className="decl-progress-bar">
                <span style={{ width: `${preview.completion || 0}%` }} />
              </div>
              <b>{preview.completion}% listo</b>
              <em>· Confianza {preview.confidence}%</em>
              <button type="button" className="decl-progress-link" onClick={() => setShowChecklist(true)} data-testid="declarations-open-checklist">
                Ver qué falta <ChevronRight size={14} />
              </button>
            </div>

            {/* Comparison */}
            {preview.comparison && (
              <div className="decl-comparison" data-testid="declarations-comparison">
                <header>
                  <b>Comparativa vs {preview.comparison.previous_label}</b>
                  <small>Detecta meses raros antes de presentar</small>
                </header>
                <div className="decl-comparison-grid">
                  {[
                    ["income", "Ingresos"],
                    ["expenses", "Egresos"],
                    ["iva_neto", "IVA a pagar"],
                    ["isr_estimado", "ISR"],
                    ["total", "Total"],
                  ].map(([key, label]) => {
                    const dd = preview.comparison.deltas[key] || {};
                    const pct = dd.pct;
                    const up = dd.delta > 0;
                    const tone = pct === null ? "neutral" : up ? "up" : dd.delta < 0 ? "down" : "neutral";
                    const Icon = tone === "up" ? TrendingUp : tone === "down" ? TrendingDown : Minus;
                    return (
                      <article key={key} className={`decl-delta decl-delta-${tone}`} data-testid={`declarations-delta-${key}`}>
                        <small>{label}</small>
                        <b>{money(dd.current)}</b>
                        <span><Icon size={12} /> {pct === null ? "—" : `${up ? "+" : ""}${pct}%`} <em>vs {money(dd.previous)}</em></span>
                      </article>
                    );
                  })}
                </div>
                {preview.comparison.anomalies.length > 0 && (
                  <ul className="decl-anomalies" data-testid="declarations-anomalies">
                    {preview.comparison.anomalies.map((a) => (
                      <li key={a.key} className={`decl-anomaly-${a.level}`}>
                        <AlertCircle size={14} /> {a.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Main KPIs including retenciones + saldo a favor */}
            <div className="decl-kpis" data-testid="declarations-kpis">
              <article><small>💰 Ingresos</small><b>{money(s.income)}</b></article>
              <article><small>🧾 Egresos</small><b>{money(s.expenses)}</b></article>
              <article>
                <small>🟢 ISR estimado <Tip text={HELP_TEXTS.isr} testid="declarations-tip-isr" /></small>
                <b>{money(s.isr_estimado)}</b>
              </article>
              <article>
                <small>🔵 IVA trasladado <Tip text={HELP_TEXTS.iva} testid="declarations-tip-iva" /></small>
                <b data-testid="declarations-iva-trasladado">{money(s.iva_transferred || s.iva_neto)}</b>
              </article>
              <article data-testid="declarations-iva-retenido-kpi">
                <small>🟠 IVA retenido</small>
                <b>-{money(s.iva_retenido || 0)}</b>
              </article>
              <article>
                <small>💧 IVA a pagar</small>
                <b data-testid="declarations-iva-a-pagar">{money(s.iva_a_pagar ?? s.iva_neto)}</b>
              </article>
              <article className="decl-kpi-retenciones" data-testid="declarations-kpi-retenciones">
                <small>🟣 Retenciones <Tip text={HELP_TEXTS.retenciones} testid="declarations-tip-retenciones" /></small>
                <b>-{money(s.retenciones_total || 0)}</b>
              </article>
              {hasSaldoFavor ? (
                <article className="highlight decl-kpi-favor" data-testid="declarations-kpi-saldo-favor">
                  <small>🟢 SALDO A FAVOR <Tip text={HELP_TEXTS.saldo_favor} testid="declarations-tip-favor" /></small>
                  <b>{money(s.saldo_a_favor)}</b>
                </article>
              ) : (
                <article className="highlight" data-testid="declarations-kpi-total">
                  <small>💵 TOTAL A PAGAR</small>
                  <b>{money(s.total)}</b>
                </article>
              )}
            </div>

            <small className="decl-disclaimer decl-warn" data-testid="declarations-disclaimer">
              <Info size={13} /> Este cálculo es una estimación basada en la información registrada en Mi Contador y debe ser revisado antes de presentar la declaración ante el SAT.
            </small>

            {/* Retenciones breakdown */}
            {(s.retenciones_total || 0) > 0 && (
              <div className="decl-retenciones-panel" data-testid="declarations-retenciones-panel">
                <b>🟣 Retenciones del período</b>
                <ul>
                  <li><span>ISR retenido por clientes</span><em>-{money(s.isr_retenido)}</em></li>
                  <li><span>IVA retenido</span><em>-{money(s.iva_retenido)}</em></li>
                </ul>
              </div>
            )}

            {/* Cancelled invoices recap */}
            {(preview.cancelled_invoices || []).length > 0 && (
              <div className="decl-cancelled-panel" data-testid="declarations-cancelled-panel">
                <header>
                  <XCircle size={16} />
                  <div>
                    <b>Facturas canceladas en el mes ({preview.cancelled_invoices.length})</b>
                    <small>Explican por qué los números cambiaron respecto al mes anterior</small>
                  </div>
                </header>
                <table className="decl-cancelled-table">
                  <thead><tr><th>Folio</th><th>Cliente</th><th>Motivo</th><th>Cancelada</th><th className="right">Monto</th></tr></thead>
                  <tbody>
                    {preview.cancelled_invoices.slice(0, 20).map((inv) => (
                      <tr key={inv.id} data-testid={`declarations-cancelled-${inv.id}`}>
                        <td><b>{inv.folio || (inv.id || "").slice(0, 8)}</b>{inv.substitution_uuid && <em title="Sustituida por otro CFDI"> · sust.</em>}</td>
                        <td>{inv.client_name || "—"}<small>{inv.client_rfc || ""}</small></td>
                        <td>
                          {inv.motive && <span className="decl-motive-code">{inv.motive}</span>}{" "}
                          <span>{inv.motive_label || inv.reason || "Sin motivo"}</span>
                        </td>
                        <td>{(inv.cancelled_at || "").slice(0, 10) || "—"}</td>
                        <td className="right">{money(inv.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.cancelled_invoices.length > 20 && (
                  <small className="decl-cancelled-more">+ {preview.cancelled_invoices.length - 20} más — <button type="button" className="link" onClick={() => navigate("/facturas?status=cancelled")}>ver todas</button></small>
                )}
              </div>
            )}

            {/* Smart alerts panel */}
            <div className="decl-alerts" data-testid="declarations-alerts">
              <header>
                <b>🔎 Revisión antes de declarar</b>
                <small>Mi Contador revisó tus datos del período</small>
              </header>
              <ul>
                {alerts.map((a) => (
                  <AlertItem key={a.key} alert={a} onAction={(to) => navigate(to)} />
                ))}
              </ul>
            </div>

            <div className="decl-counts">
              <TrendingUp size={13} />
              {s.counts?.invoices_sent || 0} CFDI emitidos · {s.counts?.invoices_sent_cancelled || 0} canceladas · {s.counts?.invoices_received || 0} CFDI recibidos ·
              {" "}{s.counts?.incomes || 0} ingresos · {s.counts?.expenses || 0} egresos ·
              {" "}{s.counts?.cash_movements || 0} movimientos de caja · {s.counts?.receipts_available || 0} comprobantes
            </div>

            <div className="decl-actions">
              <button type="button" className="cash-btn primary" onClick={() => setShowPrepare(true)} disabled={preparing} data-testid="declarations-prepare-button">
                <Sparkles size={16} /> {preview.saved ? "Actualizar declaración" : "Revisar mi declaración"}
              </button>
              {preview.saved && preview.id && (
                <>
                  <button type="button" className="cash-btn ghost" onClick={() => openDetail(preview.id)} data-testid="declarations-open-detail">
                    <FileText size={14} /> Ver resumen completo
                  </button>
                  <button type="button" className="cash-btn ghost" onClick={() => downloadPdf(preview.id, preview.period)} data-testid="declarations-download-current">
                    <Download size={14} /> Descargar PDF
                  </button>
                  <button type="button" className="cash-btn ghost" onClick={() => downloadXmlPack(preview.id, preview.period)} data-testid="declarations-download-xml-pack">
                    <FileArchive size={14} /> Descargar XML SAT
                  </button>
                  <a className="cash-btn ghost" href={`mailto:?subject=Declaración ${preview.period_label}&body=Hola, adjunto la declaración provisional del mes.`} data-testid="declarations-send-current">
                    <Send size={14} /> Enviar a contador
                  </a>
                  <button type="button" className="cash-btn ghost" onClick={() => setShowPresented(preview.id)} data-testid="declarations-mark-presented">
                    <Stamp size={14} /> Marcar como presentada
                  </button>
                </>
              )}
            </div>
          </section>
        )}

        <section className="decl-ytd" data-testid="declarations-ytd">
          <header>
            <div>
              <CalendarRange size={16} />
              <div>
                <h3>Comparativa anual</h3>
                <small>Tendencia mes a mes {ytdYear}</small>
              </div>
            </div>
            <div className="decl-ytd-year">
              <button type="button" onClick={() => setYtdYear((y) => y - 1)} data-testid="declarations-ytd-prev">‹</button>
              <b>{ytdYear}</b>
              <button type="button" onClick={() => setYtdYear((y) => y + 1)} disabled={ytdYear >= currentYear} data-testid="declarations-ytd-next">›</button>
            </div>
          </header>
          {ytd?.months && (
            <>
              <div className="decl-ytd-totals" data-testid="declarations-ytd-totals">
                <article><small>Ingresos {ytdYear}</small><b>{money(ytd.totals.income)}</b></article>
                <article><small>Egresos</small><b>{money(ytd.totals.expenses)}</b></article>
                <article><small>Deducibles</small><b>{money(ytd.totals.deductible)}</b></article>
                <article><small>IVA acumulado</small><b>{money(ytd.totals.iva_neto)}</b></article>
                <article><small>ISR acumulado</small><b>{money(ytd.totals.isr_estimado)}</b></article>
                <article className="highlight"><small>Total del año</small><b>{money(ytd.totals.total)}</b></article>
              </div>
              <div className="decl-ytd-chart" data-testid="declarations-ytd-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={ytd.months} margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => money(value)} contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" name="Ingresos" fill="#10b981" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="expenses" name="Egresos" fill="#ef4444" radius={[6, 6, 0, 0]} />
                    <Line type="monotone" dataKey="isr_estimado" name="ISR" stroke="#4169e1" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="iva_neto" name="IVA neto" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="decl-ytd-table-wrap">
                <table className="decl-ytd-table" data-testid="declarations-ytd-table">
                  <thead><tr><th>Mes</th><th className="right">Ingresos</th><th className="right">Egresos</th><th className="right">Deducibles</th><th className="right">IVA</th><th className="right">ISR</th><th className="right">Total</th></tr></thead>
                  <tbody>
                    {ytd.months.map((m) => (
                      <tr key={m.period} data-testid={`declarations-ytd-row-${m.period}`}>
                        <td>{m.month_full}</td>
                        <td className="right">{money(m.income)}</td>
                        <td className="right">{money(m.expenses)}</td>
                        <td className="right">{money(m.deductible)}</td>
                        <td className="right">{money(m.iva_neto)}</td>
                        <td className="right">{money(m.isr_estimado)}</td>
                        <td className="right"><b>{money(m.total)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className="decl-history" data-testid="declarations-history">
          <h3>Historial</h3>
          {history.length === 0 ? (
            <p className="inv-empty-mini">Aún no has guardado ninguna declaración. Empieza preparando la de este mes.</p>
          ) : (
            historyByYear.map(([year, rows]) => (
              <article key={year} className="decl-year">
                <header>{year}</header>
                <ul>
                  {rows.map((row) => (
                    <li key={row.id} data-testid={`declarations-row-${row.id}`}>
                      <div>
                        <b>{row.period_label}</b>
                        <small>{money(row.summary?.total)} · Confianza {row.confidence}%{row.presented && row.presented_at ? ` · Presentada ${row.presented_at}` : ""}</small>
                      </div>
                      <StatusBadge status={row.presented ? "presented" : row.status} testid={`declarations-row-status-${row.id}`} />
                      <button type="button" className="cash-btn ghost" onClick={() => openDetail(row.id)} data-testid={`declarations-detail-${row.id}`}>
                        <FileText size={13} /> Detalle
                      </button>
                      <button type="button" className="cash-btn ghost" onClick={() => downloadPdf(row.id, row.period)} data-testid={`declarations-download-${row.id}`}>
                        <Download size={13} /> PDF
                      </button>
                      <button type="button" className="cash-btn ghost" onClick={() => downloadXmlPack(row.id, row.period)} data-testid={`declarations-xml-${row.id}`}>
                        <FileArchive size={13} /> XML
                      </button>
                      {!row.presented && (
                        <button type="button" className="cash-btn ghost" onClick={() => setShowPresented(row.id)} data-testid={`declarations-mark-presented-${row.id}`}>
                          <Stamp size={13} /> Presentada
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </article>
            ))
          )}
        </section>

        {/* Detail modal */}
        {showDetail && (
          <div className="cash-modal-backdrop" onClick={() => setShowDetail(false)}>
            <div className="cash-modal decl-detail-modal" onClick={(e) => e.stopPropagation()} data-testid="declarations-detail-modal">
              <header>
                <div>
                  <small>DESGLOSE LÍNEA POR LÍNEA</small>
                  <b>{detail?.declaration?.period_label || "Detalle"} · {detail?.declaration?.regime || ""}</b>
                </div>
                <button type="button" onClick={() => setShowDetail(false)}><X size={18} /></button>
              </header>
              <div className="decl-detail-body">
                {detailLoading && <div className="import-loading"><Loader2 className="spin" size={18} /><span>Cargando desglose...</span></div>}
                {detail && (
                  <>
                    <div className="decl-detail-summary">
                      <article><small>Ingresos</small><b>{money(detail.summary?.income)}</b></article>
                      <article><small>Egresos</small><b>{money(detail.summary?.expenses)}</b></article>
                      <article><small>Deducibles</small><b>{money(detail.summary?.deductible)}</b></article>
                      <article className="highlight"><small>Total a pagar</small><b>{money(detail.summary?.total)}</b></article>
                    </div>

                    <section className="decl-detail-section" data-testid="declarations-detail-income">
                      <h4>Ingresos ({detail.income_lines.length})</h4>
                      {detail.income_lines.length === 0 ? (
                        <p className="inv-empty-mini">Sin ingresos registrados en el período.</p>
                      ) : (
                        <table className="decl-detail-table">
                          <thead><tr><th>Fecha</th><th>Fuente</th><th>Concepto</th><th>Cliente</th><th className="right">Monto</th></tr></thead>
                          <tbody>
                            {detail.income_lines.map((l, i) => (
                              <tr key={`in-${i}`} className={l.excluded ? "excluded" : ""} data-testid={`declarations-detail-income-${i}`}>
                                <td>{l.date}</td>
                                <td><span className={`decl-src decl-src-${l.source}`}>{l.source.replace("_", " ")}</span>{l.meta?.has_invoice && <em title="Con CFDI"> · CFDI</em>}</td>
                                <td>{l.concept}</td>
                                <td>{l.party || "—"}</td>
                                <td className="right">{money(l.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </section>

                    <section className="decl-detail-section" data-testid="declarations-detail-expenses">
                      <h4>Egresos ({detail.expense_lines.length})</h4>
                      {detail.expense_lines.length === 0 ? (
                        <p className="inv-empty-mini">Sin egresos registrados en el período.</p>
                      ) : (
                        <table className="decl-detail-table">
                          <thead><tr><th>Fecha</th><th>Fuente</th><th>Concepto</th><th>Proveedor</th><th>Estatus</th><th className="right">Monto</th></tr></thead>
                          <tbody>
                            {detail.expense_lines.map((l, i) => (
                              <tr key={`ex-${i}`} className={l.excluded ? "excluded" : ""} data-testid={`declarations-detail-expense-${i}`}>
                                <td>{l.date}</td>
                                <td><span className={`decl-src decl-src-${l.source}`}>{l.source.replace("_", " ")}</span></td>
                                <td>{l.concept}</td>
                                <td>{l.party || "—"}</td>
                                <td>{l.meta?.deductible ? <span className="decl-tag ok">Deducible</span> : <span className="decl-tag warn">Revisar</span>}</td>
                                <td className="right">{money(l.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </section>

                    <section className="decl-detail-section" data-testid="declarations-detail-receipts">
                      <h4>Comprobantes ({detail.receipts.length})</h4>
                      {detail.receipts.length === 0 ? (
                        <p className="inv-empty-mini">Sin comprobantes en Bóveda para este período.</p>
                      ) : (
                        <ul className="decl-detail-receipts">
                          {detail.receipts.map((r) => (
                            <li key={r.id}><FileText size={14} /><span>{r.filename}</span><small>{r.created_at?.slice(0, 10)}</small></li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Checklist modal */}
        {showChecklist && preview && (
          <div className="cash-modal-backdrop" onClick={() => setShowChecklist(false)}>
            <div className="cash-modal" onClick={(e) => e.stopPropagation()} data-testid="declarations-checklist-modal">
              <header>
                <div>
                  <small>QUÉ FALTA</small>
                  <b>Checklist de {preview.period_label}</b>
                </div>
                <button type="button" onClick={() => setShowChecklist(false)}><X size={18} /></button>
              </header>
              <div style={{ padding: "8px 24px 24px" }}>
                <ul className="decl-checklist" data-testid="declarations-checklist-items">
                  {preview.checklist.map((item) => (
                    <li key={item.key} className={item.done ? "done" : ""} data-testid={`declarations-check-${item.key}`}>
                      {item.done ? <CheckCircle2 size={18} className="check" /> : <Circle size={18} className="empty" />}
                      <div>
                        <b>{item.label}</b>
                        {item.hint && <small>{item.hint}</small>}
                      </div>
                      {item.action && !item.done && item.action.kind === "navigate" && (
                        <button type="button" onClick={() => { setShowChecklist(false); navigate(item.action.to); }} data-testid={`declarations-action-${item.key}`}>
                          {item.action.label} <ArrowRight size={13} />
                        </button>
                      )}
                      {item.action && item.action.kind === "info" && (
                        <span className="decl-info-badge">{item.action.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Prepare modal with acknowledgement checklist */}
        {showPrepare && preview && (
          <div className="cash-modal-backdrop" onClick={() => setShowPrepare(false)}>
            <div className="cash-modal" onClick={(e) => e.stopPropagation()} data-testid="declarations-prepare-modal">
              <header>
                <div>
                  <small>PREPARAR</small>
                  <b>{preview.period_label}</b>
                </div>
                <button type="button" onClick={() => setShowPrepare(false)}><X size={18} /></button>
              </header>
              <div style={{ padding: "8px 24px 24px" }}>
                <p style={{ color: "#4b5563", marginBottom: 16 }}>Confirma antes de calcular:</p>
                <fieldset className="cash-status-picker" style={{ gridTemplateColumns: "1fr", gap: 8 }} data-testid="declarations-zero-income">
                  <legend>¿Tuviste ingresos este mes?</legend>
                  <label className={!prepareAnswers.declare_zero_income ? "active" : ""}>
                    <input type="radio" checked={!prepareAnswers.declare_zero_income} onChange={() => setPrepareAnswers({ ...prepareAnswers, declare_zero_income: false })} data-testid="declarations-zero-income-yes" />
                    <div><b>Sí, usa mis registros</b><small>Sumaremos CFDI, ingresos y caja fiscal del mes.</small></div>
                  </label>
                  <label className={prepareAnswers.declare_zero_income ? "active" : ""}>
                    <input type="radio" checked={prepareAnswers.declare_zero_income} onChange={() => setPrepareAnswers({ ...prepareAnswers, declare_zero_income: true })} data-testid="declarations-zero-income-no" />
                    <div><b>No, declarar en $0</b><small>Genera una declaración en ceros aunque haya registros.</small></div>
                  </label>
                </fieldset>
                <fieldset className="cash-status-picker" style={{ gridTemplateColumns: "1fr", gap: 8 }} data-testid="declarations-zero-expenses">
                  <legend>¿Tuviste egresos este mes?</legend>
                  <label className={!prepareAnswers.declare_zero_expenses ? "active" : ""}>
                    <input type="radio" checked={!prepareAnswers.declare_zero_expenses} onChange={() => setPrepareAnswers({ ...prepareAnswers, declare_zero_expenses: false })} data-testid="declarations-zero-expenses-yes" />
                    <div><b>Sí, usa mis registros</b><small>Consideramos gastos, CFDI recibidos y caja.</small></div>
                  </label>
                  <label className={prepareAnswers.declare_zero_expenses ? "active" : ""}>
                    <input type="radio" checked={prepareAnswers.declare_zero_expenses} onChange={() => setPrepareAnswers({ ...prepareAnswers, declare_zero_expenses: true })} data-testid="declarations-zero-expenses-no" />
                    <div><b>No, declarar en $0</b><small>Genera la declaración sin gastos deducibles.</small></div>
                  </label>
                </fieldset>

                <fieldset className="decl-ack" data-testid="declarations-ack">
                  <legend>Antes de continuar</legend>
                  {[
                    ["ingresos", "Revisé mis ingresos"],
                    ["gastos", "Revisé mis gastos"],
                    ["facturas", "Revisé mis facturas"],
                    ["movimientos", "Revisé mis movimientos pendientes"],
                    ["estimacion", "Entiendo que este cálculo es una estimación"],
                  ].map(([key, label]) => (
                    <label key={key} className={ack[key] ? "checked" : ""}>
                      <input
                        type="checkbox"
                        checked={ack[key]}
                        onChange={(e) => setAck({ ...ack, [key]: e.target.checked })}
                        data-testid={`declarations-ack-${key}`}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="cash-modal-actions">
                  <button type="button" className="cash-btn ghost" onClick={() => setShowPrepare(false)}>Cancelar</button>
                  <button type="button" className="cash-btn primary" onClick={prepare} disabled={preparing || !canPrepare} data-testid="declarations-prepare-confirm">
                    {preparing ? "Preparando..." : "Preparar y guardar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mark as presented modal */}
        {showPresented && (
          <div className="cash-modal-backdrop" onClick={() => setShowPresented(null)}>
            <div className="cash-modal" onClick={(e) => e.stopPropagation()} data-testid="declarations-presented-modal">
              <header>
                <div><small>PRESENTADA MANUALMENTE</small><b>Confirmar</b></div>
                <button type="button" onClick={() => setShowPresented(null)}><X size={18} /></button>
              </header>
              <div style={{ padding: "8px 24px 24px" }}>
                <p style={{ color: "#4b5563", marginBottom: 16 }}>
                  Si ya presentaste esta declaración en <b>sat.gob.mx</b>, márcala como presentada para llevar registro. Mi Contador no la envía por ti.
                </p>
                <label className="decl-receipt-label">
                  <span>Número de acuse (opcional)</span>
                  <input
                    type="text"
                    placeholder="Ej. ABC-1234"
                    data-testid="declarations-presented-receipt"
                    onKeyDown={(e) => { if (e.key === "Enter") markPresented(showPresented, e.target.value); }}
                  />
                </label>
                <div className="cash-modal-actions">
                  <button type="button" className="cash-btn ghost" onClick={() => setShowPresented(null)}>Cancelar</button>
                  <button
                    type="button"
                    className="cash-btn primary"
                    onClick={() => {
                      const input = document.querySelector('[data-testid="declarations-presented-receipt"]');
                      markPresented(showPresented, input?.value || "");
                    }}
                    data-testid="declarations-presented-confirm"
                  >
                    Marcar como presentada
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
